import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import useAnalyticsWS from '../../hooks/useAnalyticsWS';
import PulseNumber from '../../components/PulseNumber';
import { Loader2, RefreshCcw, Building2, ArrowDown, ArrowUp, Gamepad2 } from 'lucide-react';
import { getFinanceToken } from './FinanceAuthPage';

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'all', label: 'All Time' },
];

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtFP = (n) => `${Math.round(Number(n || 0)).toLocaleString()} FP`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

function financeHeaders() {
  const t = getFinanceToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * FinanceTenantAnalytics — read-only tenant KPI panel for finance staff.
 * Mirrors the admin TenantAnalyticsPage but uses finance bearer auth and
 * trims the UI to the numbers finance actually needs (no edit / drill-down).
 */
export default function FinanceTenantAnalytics() {
  const [period, setPeriod] = useState('today');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [providerData, setProviderData] = useState({});
  const [providerLoading, setProviderLoading] = useState({});
  const [lastTick, setLastTick] = useState(new Date());

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/api/admin/tenant-analytics/overview?period=${period}`,
        { headers: financeHeaders() },
      );
      setOverview(res.data);
      setLastTick(new Date());
    } catch {
      setOverview(null);
    } finally {
      setLoading(false);
    }
  };

  const loadProviders = async (tenantId) => {
    const key = tenantId || 'main';
    setProviderLoading((l) => ({ ...l, [key]: true }));
    try {
      const qs = new URLSearchParams({ period });
      if (tenantId) qs.set('tenant_id', tenantId);
      const res = await api.get(
        `/api/admin/tenant-analytics/providers?${qs.toString()}`,
        { headers: financeHeaders() },
      );
      setProviderData((d) => ({ ...d, [key]: res.data }));
    } catch {
      /* no-op */
    } finally {
      setProviderLoading((l) => ({ ...l, [key]: false }));
    }
  };

  useEffect(() => {
    load();
    // poll every 60s — finance portal is lighter-weight, no need for 30s.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Real-time WS push for "today" period (server ticks every ~15s).
  const wsStatus = useAnalyticsWS({
    token: getFinanceToken(),
    enabled: period === 'today',
    onSnapshot: (snap) => {
      setOverview(snap);
      setLastTick(new Date());
    },
  });

  const rows = overview?.rows || [];
  const grand = overview?.grand_total || {};

  const toggleExpand = (tenantId) => {
    const key = tenantId || 'main';
    if (expanded === key) {
      setExpanded(null);
      return;
    }
    setExpanded(key);
    if (!providerData[key]) loadProviders(tenantId);
  };

  const lastSync = useMemo(
    () =>
      lastTick.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    [lastTick],
  );

  return (
    <div data-testid="finance-tenant-analytics">
      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              data-testid={`analytics-period-${p.id}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                period === p.id
                  ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                  : 'bg-[#131822] border-white/10 text-gray-400 hover:text-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[11px] text-gray-500 flex items-center gap-2">
            Synced {lastSync}
            {period === 'today' && wsStatus.current === 'open' ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400"
                data-testid="analytics-live-badge"
                title="Real-time updates via WebSocket"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                LIVE
              </span>
            ) : (
              <span className="text-gray-600">· auto-refresh 60s</span>
            )}
          </div>
          <button
            onClick={load}
            className="text-xs text-gray-400 hover:text-blue-400 flex items-center gap-1"
            data-testid="analytics-refresh-btn"
          >
            <RefreshCcw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {[
          { label: 'Users', value: fmtNum(grand.users), raw: grand.users, icon: Building2, color: 'text-blue-300' },
          {
            label: 'Deposits',
            value: fmtUSD(grand.deposits_usd),
            raw: grand.deposits_usd,
            icon: ArrowDown,
            color: 'text-green-300',
          },
          {
            label: 'Withdrawals',
            value: fmtUSD(grand.withdrawals_usd),
            raw: grand.withdrawals_usd,
            icon: ArrowUp,
            color: 'text-red-300',
          },
          {
            label: 'FP Issued',
            value: fmtFP(grand.fp_issued),
            raw: grand.fp_issued,
            icon: Gamepad2,
            color: 'text-purple-300',
          },
          {
            label: 'FP Outstanding',
            value: fmtFP(grand.fp_outstanding),
            raw: grand.fp_outstanding,
            icon: Gamepad2,
            color: 'text-amber-300',
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-xl bg-[#131822] border border-white/10 p-3"
            data-testid={`analytics-kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`flex items-center gap-2 text-[11px] ${kpi.color}`}>
              <kpi.icon className="w-3.5 h-3.5" /> {kpi.label}
            </div>
            <div className="text-white text-lg font-bold tabular-nums mt-1">
              <PulseNumber value={Number(kpi.raw) || 0} format={() => kpi.value} />
            </div>
          </div>
        ))}
      </div>

      {/* Per-tenant table */}
      {loading ? (
        <div className="text-gray-400 flex items-center gap-2 p-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center text-gray-500 py-16">No analytics yet.</div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#131822] text-gray-400">
              <tr>
                <th className="text-left px-3 py-2">Tenant</th>
                <th className="text-right px-3 py-2">Users</th>
                <th className="text-right px-3 py-2">Deposits</th>
                <th className="text-right px-3 py-2">Withdrawals</th>
                <th className="text-right px-3 py-2">Cash Bal</th>
                <th className="text-right px-3 py-2">FP Issued</th>
                <th className="text-right px-3 py-2">FP Outstanding</th>
                <th className="text-center px-3 py-2"> </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = r.tenant_id || 'main';
                const open = expanded === key;
                const pd = providerData[key];
                const pl = providerLoading[key];
                return (
                  <>
                    <tr
                      key={key}
                      className="border-t border-white/5 hover:bg-white/5 transition"
                      data-testid={`analytics-row-${r.tenant_slug}`}
                    >
                      <td className="px-3 py-2 text-white font-medium">
                        {r.tenant_slug === 'main' ? '★ ' : ''}
                        {r.tenant_name}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtNum(r.users)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-green-300">
                        {fmtUSD(r.deposits?.usd)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-300">
                        {fmtUSD(r.withdrawals?.usd)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-300">
                        {fmtUSD(r.cash_balance)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-purple-300">
                        {fmtFP(r.fp_issued)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-300">
                        {fmtFP(r.fp_outstanding)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          className="text-xs text-blue-400 hover:underline"
                          onClick={() => toggleExpand(r.tenant_id)}
                          data-testid={`analytics-expand-${r.tenant_slug}`}
                        >
                          {open ? 'Hide' : 'Providers'}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-black/30">
                        <td colSpan={8} className="px-3 py-3">
                          {pl ? (
                            <div className="text-gray-400 flex items-center gap-2 text-xs">
                              <Loader2 className="w-3 h-3 animate-spin" /> Loading providers…
                            </div>
                          ) : pd && pd.rows.length > 0 ? (
                            <table className="w-full text-xs">
                              <thead className="text-gray-500">
                                <tr>
                                  <th className="text-left py-1">Provider</th>
                                  <th className="text-right py-1">Loads</th>
                                  <th className="text-right py-1">Load $</th>
                                  <th className="text-right py-1">Redeems</th>
                                  <th className="text-right py-1">Redeem $</th>
                                  <th className="text-right py-1">Net</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pd.rows.map((pr) => (
                                  <tr key={pr.provider} className="border-t border-white/5">
                                    <td className="py-1 text-gray-200">{pr.provider}</td>
                                    <td className="py-1 text-right tabular-nums">{fmtNum(pr.load_count)}</td>
                                    <td className="py-1 text-right tabular-nums text-green-300">
                                      {fmtUSD(pr.load_sc)}
                                    </td>
                                    <td className="py-1 text-right tabular-nums">{fmtNum(pr.redeem_count)}</td>
                                    <td className="py-1 text-right tabular-nums text-red-300">
                                      {fmtUSD(pr.redeem_sc)}
                                    </td>
                                    <td
                                      className={`py-1 text-right tabular-nums ${
                                        pr.net_sc >= 0 ? 'text-green-300' : 'text-red-300'
                                      }`}
                                    >
                                      {fmtUSD(pr.net_sc)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="text-xs text-gray-500">No provider activity.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
